import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { MessageService } from "@openng/optimus-ui/api";
import { TenantAdministrationService } from "../../../../../services/api/tenant-administration/tenant-administration.service";
import { first } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
  selector: "app-tenant-settings",
  standalone: true,
  imports: [ReactiveFormsModule, InputTextModule, TranslateModule, ButtonComponent],
  templateUrl: "./tenant-settings.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./tenant-settings.component.scss",
})
export class TenantSettingsComponent {
  public ref = inject(DynamicDialogRef);
  public requestStarted = signal(false);
  private tenantService = inject(TenantAdministrationService);
  private translate = inject(TranslateService);
  private config = inject(DynamicDialogConfig);
  private message = inject(MessageService);
  private tenant = this.config.data;
  public form: FormGroup = new FormGroup({
    notificationTimeFE: new FormControl(this.tenant?.notificationTimeFE ?? 0, [Validators.min(0), Validators.max(999)]),
    notificationTimeGuard: new FormControl(this.tenant?.notificationTimeGuard ?? 0, [
      Validators.min(0),
      Validators.max(999),
    ]),
  });

  /**
   * Validates the form and saves the tenant settings. If the form is invalid, it displays an error message.
   */
  public save() {
    const formValues = this.form?.getRawValue();

    if (this.form.valid && formValues) {
      if (!this.requestStarted()) {
        this.requestStarted.set(true);
        this.tenantService
          .editTenantSettings({ id: this.tenant.id, ...formValues })
          .pipe(first())
          .subscribe({
            next: () => {
              this.requestStarted.set(false);
              this.ref.close(true);

              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("gate_administration.tenant_settings.success"),
                life: 4500,
              });
            },
            error: (_: HttpErrorResponse) => {
              this.requestStarted.set(false);
            },
          });
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }
}
